/**
 * Conversations Page
 * View child's conversation history with AI tutor
 */

import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, MessageSquare, Calendar, Flag, ChevronDown, ChevronUp, Search } from 'lucide-react';
import { analyticsService, childService, getErrorMessage } from '../services';
import type { ConversationLog } from '../services/analyticsService';
import type { ChildProfileWithConsent } from '@kids-lms/types';

export default function ConversationsPage() {
  const { childId } = useParams<{ childId: string }>();
  const [child, setChild] = useState<ChildProfileWithConsent | null>(null);
  const [conversations, setConversations] = useState<ConversationLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedConversation, setExpandedConversation] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterFlagged, setFilterFlagged] = useState(false);

  useEffect(() => {
    if (childId) {
      loadData();
    }
  }, [childId]);

  const loadData = async () => {
    if (!childId) return;

    try {
      setLoading(true);

      // Load child and conversations independently so one failure doesn't block the other
      const [childResult, logsResult] = await Promise.allSettled([
        childService.getChild(childId),
        analyticsService.getChildLogs(childId, { limit: 50 }),
      ]);

      if (childResult.status === 'fulfilled') {
        setChild(childResult.value);
      }

      if (logsResult.status === 'fulfilled') {
        setConversations(logsResult.value.conversations || []);
      } else {
        setError(getErrorMessage(logsResult.reason));
      }
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const toggleConversation = (id: string) => {
    setExpandedConversation(expandedConversation === id ? null : id);
  };

  const filteredConversations = conversations.filter((conv) => {
    if (filterFlagged && !conv.flagged) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        conv.subject?.toLowerCase().includes(query) ||
        (conv.messages || []).some((m) => m.content?.toLowerCase().includes(query))
      );
    }
    return true;
  });

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-kid-ocean border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-6">
        <Link
          to="/parent"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </Link>
      </div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-kid-violet/10 rounded-xl flex items-center justify-center">
            <MessageSquare className="w-6 h-6 text-kid-violet" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {child?.name}'s Conversations
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              View learning conversations with AI tutor
            </p>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-400 rounded-xl">
            {error}
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search conversations..."
              className="w-full pl-10 pr-4 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={filterFlagged}
              onChange={(e) => setFilterFlagged(e.target.checked)}
              className="w-5 h-5 rounded border-gray-300 text-kid-tangerine focus:ring-kid-tangerine"
            />
            <span className="text-gray-700 dark:text-gray-300">Show flagged only</span>
          </label>
        </div>

        {/* Conversations List */}
        {filteredConversations.length === 0 ? (
          <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-2xl shadow-lg">
            <MessageSquare className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              No Conversations Found
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              {filterFlagged
                ? 'No flagged conversations'
                : "This child hasn't had any conversations yet"}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredConversations.map((conv) => (
              <motion.div
                key={conv.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className={`bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden ${
                  conv.flagged ? 'border-l-4 border-l-red-500' : ''
                }`}
              >
                <button
                  onClick={() => toggleConversation(conv.id)}
                  className="w-full p-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div
                      className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                        conv.flagged
                          ? 'bg-red-100 dark:bg-red-900/30'
                          : 'bg-kid-ocean/10'
                      }`}
                    >
                      {conv.flagged ? (
                        <Flag className="w-5 h-5 text-red-500" />
                      ) : (
                        <MessageSquare className="w-5 h-5 text-kid-ocean" />
                      )}
                    </div>
                    <div className="text-left">
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        {conv.subject || 'General Chat'}
                      </h3>
                      <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(conv.createdAt)}
                        </span>
                        <span className="flex items-center gap-1">
                          <MessageSquare className="w-4 h-4" />
                          {conv.messageCount} messages
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {conv.flagged && (
                      <span className="px-2 py-1 text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-full">
                        {conv.flagCount} flags
                      </span>
                    )}
                    {expandedConversation === conv.id ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </button>

                {/* Expanded Messages */}
                {expandedConversation === conv.id && (
                  <div className="border-t border-gray-100 dark:border-gray-700 p-4 space-y-3 max-h-96 overflow-y-auto">
                    {(conv.messages || []).length === 0 && (
                      <p className="text-center text-gray-500 dark:text-gray-400 py-4">No messages available</p>
                    )}
                    {(conv.messages || []).map((msg) => (
                      <div
                        key={msg.id}
                        className={`p-3 rounded-xl ${
                          msg.role === 'user'
                            ? 'bg-kid-ocean/10 ml-8'
                            : 'bg-gray-100 dark:bg-gray-700 mr-8'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span
                            className={`text-xs font-medium ${
                              msg.role === 'user'
                                ? 'text-kid-ocean'
                                : 'text-gray-600 dark:text-gray-400'
                            }`}
                          >
                            {msg.role === 'user' ? child?.name : 'Mindora AI'}
                          </span>
                          <span className="text-xs text-gray-400">
                            {new Date(msg.createdAt).toLocaleTimeString()}
                          </span>
                        </div>
                        <p className="text-gray-800 dark:text-gray-200 text-sm whitespace-pre-wrap">
                          {msg.content}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </motion.div>
    </div>
  );
}
